import { useState } from "react";
import { Check, Loader2, Sparkles, Package, Gift } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { money } from "@/lib/format";
import ListingDescription from "@/components/ListingDescription";

// Renders an owner's (enhancer/location) session packages with buy + gift-to-a-friend.
export default function StorefrontPackages({ packages = [], title = "Session packages" }) {
  const { user } = useAuth();
  const [busy, setBusy] = useState("");
  const [full, setFull] = useState({});
  const [giftFor, setGiftFor] = useState("");   // package id currently being gifted
  const [g, setG] = useState({ recipient_email: "", recipient_name: "", gift_message: "" });
  if (!packages.length) return null;

  const requireLogin = () => {
    if (!user) { toast.info("Please log in or join to continue."); window.location.href = "/login"; return false; }
    return true;
  };

  const checkout = async (p, isGift) => {
    if (!requireLogin()) return;
    if (isGift && !g.recipient_email) { toast.error("Add the recipient's email to send this gift."); return; }
    setBusy(p.id);
    try {
      const { data } = await api.post(`/packages/${p.id}/purchase`, {
        origin_url: window.location.origin,
        is_gift: !!isGift,
        ...(isGift ? g : {}),
      });
      if (data.url) window.location.href = data.url;
    } catch (e) {
      const d = e.response?.data?.detail;
      if (d?.code === "group_full") setFull((f) => ({ ...f, [p.id]: d.message }));
      else toast.error(formatApiError(d));
      setBusy("");
    }
  };
  const waitlist = async (p) => {
    try {
      const { data } = await api.post(`/packages/${p.id}/waitlist`);
      toast.success(`You're on the waitlist (#${data.position}). We'll email you when a new group opens.`);
      setFull((f) => ({ ...f, [p.id]: "joined" }));
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  return (
    <section className="mb-10" data-testid="storefront-packages">
      <h2 className="font-serif text-2xl text-[#1A242B] mb-4 flex items-center gap-2"><Package size={20} className="text-[#D98E73]" /> {title}</h2>
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
        {packages.map((p) => (
          <div key={p.id} className="eyw-card p-6 flex flex-col" data-testid={`storefront-package-${p.id}`}>
            <h3 className="text-lg text-[#1A242B]">{p.name}</h3>
            {p.owner_type === "enhancer" && p.owner_name && (
              <a href={`/enhancers/${p.owner_id}`} className="text-xs text-[#5C8A72] hover:underline" data-testid={`storefront-package-owner-${p.id}`}>by {p.owner_name}</a>
            )}
            <div className="mt-1 flex items-baseline gap-2">
              <span className="text-3xl font-serif text-[#144F75]">{money(Number(p.price))}</span>
              <span className="text-sm text-[#8FA89B]">· {p.sessions} sessions</span>
            </div>
            <ListingDescription text={p.description} item={p} kind="package" className="text-sm text-[#4A5D6B] mt-3 flex-1" testid={`sf-package-desc-${p.id}`} />
            {p.eligible_services?.length > 0 && (
              <div className="mt-4">
                <p className="text-xs font-medium text-[#1A242B] mb-2 flex items-center gap-1"><Sparkles size={13} className="text-[#D98E73]" /> Choose from</p>
                <ul className="space-y-1.5">
                  {p.eligible_services.slice(0, 6).map((s) => (
                    <li key={s.id} className="text-sm text-[#4A5D6B] flex items-start gap-2"><Check size={14} className="text-[#5C8A72] mt-0.5 shrink-0" /> {s.title}</li>
                  ))}
                </ul>
              </div>
            )}

            {giftFor === p.id ? (
              <div className="mt-5 space-y-2 rounded-xl border border-[#D98E73]/40 bg-[#D98E73]/[0.06] p-4" data-testid={`gift-fields-${p.id}`}>
                <p className="text-sm font-medium text-[#1A242B] flex items-center gap-1.5"><Gift size={15} className="text-[#D98E73]" /> Gift this package</p>
                <input placeholder="Recipient's email" value={g.recipient_email} onChange={(e) => setG({ ...g, recipient_email: e.target.value })} data-testid={`gift-email-${p.id}`} className="eyw-input !py-2 text-sm" />
                <input placeholder="Recipient's name (optional)" value={g.recipient_name} onChange={(e) => setG({ ...g, recipient_name: e.target.value })} data-testid={`gift-name-${p.id}`} className="eyw-input !py-2 text-sm" />
                <input placeholder="A short message (optional)" value={g.gift_message} onChange={(e) => setG({ ...g, gift_message: e.target.value })} data-testid={`gift-msg-${p.id}`} className="eyw-input !py-2 text-sm" />
                <div className="flex gap-2 pt-1">
                  <button onClick={() => checkout(p, true)} disabled={busy === p.id} data-testid={`gift-send-${p.id}`} className="eyw-btn flex-1 !py-2 text-sm disabled:opacity-60">
                    {busy === p.id ? <Loader2 className="animate-spin" size={16} /> : <Gift size={15} />} Send gift
                  </button>
                  <button onClick={() => setGiftFor("")} className="eyw-btn-ghost !py-2 !px-3 text-sm">Cancel</button>
                </div>
              </div>
            ) : (
              <div className="mt-5 space-y-2">
                {full[p.id] ? (
                  <div className="rounded-xl bg-[#144F75]/[0.06] p-3 text-sm text-[#1A242B]" data-testid={`storefront-package-full-${p.id}`}>
                    {full[p.id] === "joined" ? "You're on the waitlist — we'll email you when the next group opens." : (
                      <>{full[p.id]}<button onClick={() => waitlist(p)} data-testid={`storefront-package-waitlist-${p.id}`} className="eyw-btn w-full mt-2 !py-2 text-sm">Join the waitlist</button></>
                    )}
                  </div>
                ) : (
                  <button onClick={() => checkout(p, false)} disabled={busy === p.id} data-testid={`storefront-package-buy-${p.id}`} className="eyw-btn w-full disabled:opacity-60">
                    {busy === p.id ? <Loader2 className="animate-spin" size={18} /> : null} Purchase package
                  </button>
                )}
                <button onClick={() => { setGiftFor(p.id); setG({ recipient_email: "", recipient_name: "", gift_message: "" }); }} data-testid={`storefront-package-gift-${p.id}`} className="eyw-btn-ghost w-full !py-2 text-sm text-[#D98E73] border-[#D98E73]/40 hover:bg-[#D98E73]/[0.08]">
                  <Gift size={15} /> Gift to a friend
                </button>
              </div>
            )}
          </div>
        ))}
      </div>
    </section>
  );
}
